import React, { useState, useCallback, useRef, memo } from "react";
import { useLocalization } from "@fluent/react";
import { TextInput, Chip, useBreakpoints } from "@narmi/design_system";
import { entities } from "byzantine";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import { CashbackOfferView } from "../CardLinkedOffers/CardOfferScrollView";
import CompactOffersGrid from "../CardLinkedOffers/CompactOffersGrid";

import styles from "./CardOffersDashboard.module.scss";

interface EnrolledUserOfferContentProps {
  onOfferClick: (offerId: string) => void;
  onUnenrollClick: () => void;
}

const EnrolledUserOfferContent = memo(
  ({ onOfferClick, onUnenrollClick }: EnrolledUserOfferContentProps) => {
    const { l10n } = useLocalization();
    const { short_name: shortName, long_name: longName } =
      useInstitutionSettings();
    const { m } = useBreakpoints();
    const filterTagsRef = useRef<HTMLDivElement>(null);

    // Local state for search and filtering
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFilter, setSelectedFilter] = useState("All");

    // Data hooks
    const availableOffers = entities.cardLinkedOffers.useAllCardLinkedOffers();
    const recommendedOffers =
      entities.cardLinkedOffers.useRecommendedCardLinkedOffers();

    const fiName = shortName ?? longName;

    // Filter tag scrolling callback for mobile
    const handleFilterTagScroll = useCallback(
      (filter: string, index: number) => {
        setSelectedFilter(filter);

        // Auto-scroll on mobile devices (screens smaller than 768px)
        if (filterTagsRef.current && !m) {
          const container = filterTagsRef.current;
          const chipElements = container.children;

          if (chipElements[index]) {
            const selectedChip = chipElements[index] as HTMLElement;

            // Calculate the scroll position to bring the chip to the start
            const scrollLeft = selectedChip.offsetLeft - container.offsetLeft;

            container.scrollTo({
              left: scrollLeft,
              behavior: "smooth",
            });
          }
        }
      },
      [m]
    );

    const { filteredOffers, filters, handleFilterChange } =
      entities.cardLinkedOffers.useOfferFiltering(
        searchTerm,
        selectedFilter,
        handleFilterTagScroll
      );

    const hasOffers =
      availableOffers.length > 0 || recommendedOffers.length > 0;

    return (
      <>
        {hasOffers ? (
          <>
            {/* Recommended Offers */}
            <div className={styles.recommendedSection}>
              <div className={styles.sectionTitle}>
                {l10n.getString(
                  "card-offers-dashboard-recommended-title",
                  null,
                  "Recommended for you"
                )}
              </div>
              <CashbackOfferView
                offers={recommendedOffers}
                onOfferClick={onOfferClick}
              />
            </div>

            {/* Available Offers */}
            <div className={styles.availableOffersSection}>
              <div className={styles.availableOffersHeader}>
                <div className={styles.sectionTitle}>
                  {l10n.getString(
                    "card-offers-dashboard-available-title",
                    null,
                    "Available offers"
                  )}
                </div>
                <div className={styles.sectionDescription}>
                  {l10n.getString(
                    "card-offers-dashboard-available-description",
                    { fiName },
                    `${fiName} has card offers for a number of merchants. Search to find them. All offers are added to your card, so you dont have to add individual offers, just use your card in store or online.`
                  )}
                </div>

                {/* Search Bar */}
                <TextInput
                  startIcon="search"
                  defaultValue={searchTerm}
                  placeholder={l10n.getString(
                    "card-offers-dashboard-search-placeholder",
                    null,
                    "Search offers"
                  )}
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  endContent={
                    searchTerm.length > 0 && (
                      <div
                        className={styles.searchEndButton}
                        onClick={() => setSearchTerm("")}
                      >
                        {l10n.getString(
                          "card-offers-dashboard-clear-button",
                          null,
                          "Clear"
                        )}
                      </div>
                    )
                  }
                />

                {/* Filter Tags */}
                <div className={styles.filterTags} ref={filterTagsRef}>
                  {filters.map((filter, index) => (
                    <Chip
                      key={filter}
                      label={filter}
                      kind={selectedFilter === filter ? "primary" : "secondary"}
                      onClick={() => handleFilterChange(filter, index)}
                    />
                  ))}
                </div>
              </div>

              {/* Offers Grid or No Results */}
              {filteredOffers.length > 0 ? (
                <CompactOffersGrid
                  offers={filteredOffers}
                  className={styles.availableOffersGrid}
                  onOfferClick={onOfferClick}
                  expanded
                />
              ) : (
                <div className={styles.sectionDescription}>
                  {l10n.getString(
                    "card-offers-dashboard-no-offers-found",
                    null,
                    "No offers found."
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className={styles.availableOffersSection}>
            <div className={styles.availableOffersHeader}>
              <div className={styles.sectionTitle}>
                {l10n.getString(
                  "card-offers-dashboard-available-title",
                  null,
                  "Available offers"
                )}
              </div>
              <div className={styles.emptyStateMessage}>
                {l10n.getString(
                  "card-offers-dashboard-empty-state-message",
                  null,
                  "Offers are currently not available. Please try again later."
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          {l10n.getElement(
            <div className={styles.footerText} />,
            "card-offers-dashboard-footer-text",
            {
              elems: {
                boldedText: (
                  <span
                    className={styles.footerLink}
                    onClick={onUnenrollClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onUnenrollClick();
                      }
                    }}
                  />
                ),
              },
            }
          )}
        </div>
      </>
    );
  }
);

export default EnrolledUserOfferContent;
